export const MAX_DOCUMENTS = 9;
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_CONCURRENT_UPLOADS = 3;

export const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'audio/aac',
    'audio/mpeg',
    'audio/mp4',
    'audio/wav',
    'audio/x-wav',
    'video/mpeg',
    'video/x-flv',
];

export const ALLOWED_EXTENSIONS = [
    '.doc',
    '.docx',
    '.pdf',
    '.xls',
    '.xlsx',
    '.csv',
    '.txt',
    '.jpeg',
    '.jpg',
    '.png',
    '.gif',
    '.bmp',
    '.tif',
    '.tiff',
    '.aac',
    '.mp3',
    '.wav',
    '.m4a',
    '.amv',
    '.mpeg',
    '.mpg',
    '.flv',
];

export const BLOCKED_EXTENSIONS = ['.exe', '.js', '.vbs', '.scr', '.bat', '.cmd', '.com', '.pif', '.msi', '.jar', '.ps1'];

export const ROW_STATES = {
    UPLOADING: 'uploading',
    CHECKING: 'checking',
    SCANNED: 'scanned',
    CANNOT_BE_UPLOADED: 'cannotBeUploaded',
    FAILED: 'failed',
    REMOVED: 'removed',
};

export const MESSAGES = {
    empty: 'This file is empty. Please choose another file.',
    tooLarge: 'This file is larger than 10 MB. Please upload a smaller file.',
    notAccepted: 'This type of file cannot be uploaded. Please upload a PDF, Word, Excel, text, image or audio file.',
    tooMany: `You can attach up to ${MAX_DOCUMENTS} files to an enquiry.`,
    passwordProtected: 'This document is password protected, remove the password and try again.',
    uploading: 'Uploading',
    checking: 'We are checking your document',
    scanned: 'Scanned',
    cannotBeUploaded: 'This document cannot be uploaded',
    failed: 'Something went wrong, please try again',
    removed: 'Removed, please upload it again',
    reconnecting: 'Reconnecting',
};

export const REASON_MESSAGES = {
    passwordProtected: 'Password protected',
};

export function fileExtension(fileName) {
    if (typeof fileName !== 'string') return '';
    const dot = fileName.lastIndexOf('.');
    return dot > 0 ? fileName.slice(dot).toLowerCase() : '';
}

export function isAcceptedType(fileName, contentType) {
    const extension = fileExtension(fileName);
    if (extension && BLOCKED_EXTENSIONS.includes(extension)) return false;
    const mimeType = (contentType || '').trim().toLowerCase();
    if (mimeType && ALLOWED_MIME_TYPES.includes(mimeType)) return true;
    return !!extension && ALLOWED_EXTENSIONS.includes(extension);
}

export function checkFile(file) {
    if (!file || !file.size) return MESSAGES.empty;
    if (file.size > MAX_FILE_SIZE_BYTES) return MESSAGES.tooLarge;
    if (!isAcceptedType(file.name, file.type)) return MESSAGES.notAccepted;
    return null;
}

export function sortFiles(files, slotsUsed) {
    const accepted = [];
    const refused = [];
    let available = Math.max(0, MAX_DOCUMENTS - (slotsUsed || 0));
    (files || []).forEach((file) => {
        const problem = checkFile(file);
        if (problem) {
            refused.push({ fileName: file?.name || '', message: problem });
            return;
        }
        if (available === 0) {
            refused.push({ fileName: file.name, message: MESSAGES.tooMany });
            return;
        }
        available -= 1;
        accepted.push(file);
    });
    return { accepted, refused };
}

const PDF_ENCRYPT_MARKER = [0x2f, 0x45, 0x6e, 0x63, 0x72, 0x79, 0x70, 0x74];
const OLE_HEADER = [0xd0, 0xcf, 0x11, 0xe0];
const OOXML_EXTENSIONS = ['.docx', '.xlsx'];

function startsWith(bytes, prefix) {
    if (!bytes || bytes.length < prefix.length) return false;
    return prefix.every((value, index) => bytes[index] === value);
}

function contains(bytes, marker) {
    if (!bytes) return false;
    const last = bytes.length - marker.length;
    for (let i = 0; i <= last; i += 1) {
        let match = true;
        for (let j = 0; j < marker.length; j += 1) {
            if (bytes[i + j] !== marker[j]) {
                match = false;
                break;
            }
        }
        if (match) return true;
    }
    return false;
}

export function needsPasswordCheck(fileName) {
    const extension = fileExtension(fileName);
    return extension === '.pdf' || OOXML_EXTENSIONS.includes(extension);
}

export function isPasswordProtected(fileName, bytes) {
    const extension = fileExtension(fileName);
    if (extension === '.pdf') return contains(bytes, PDF_ENCRYPT_MARKER);
    if (OOXML_EXTENSIONS.includes(extension)) return startsWith(bytes, OLE_HEADER);
    return false;
}

export function rowStateFor(status, result, { uploadingHere = false } = {}) {
    switch (status) {
        case 'awaitingUpload':
            return uploadingHere ? ROW_STATES.UPLOADING : ROW_STATES.FAILED;
        case 'uploaded':
        case 'scanning':
            return ROW_STATES.CHECKING;
        case 'clean':
            return ROW_STATES.SCANNED;
        case 'notClean':
            return ROW_STATES.CANNOT_BE_UPLOADED;
        case 'failed':
            return result === 'cannotBeUploaded' ? ROW_STATES.CANNOT_BE_UPLOADED : ROW_STATES.FAILED;
        case 'deleted':
            return ROW_STATES.REMOVED;
        default:
            return ROW_STATES.FAILED;
    }
}

export function labelFor(row) {
    if (!row) return '';
    switch (row.state) {
        case ROW_STATES.UPLOADING:
            return MESSAGES.uploading;
        case ROW_STATES.CHECKING:
            return MESSAGES.checking;
        case ROW_STATES.SCANNED:
            return MESSAGES.scanned;
        case ROW_STATES.CANNOT_BE_UPLOADED:
            return REASON_MESSAGES[row.reason] ? `${MESSAGES.cannotBeUploaded}: ${REASON_MESSAGES[row.reason]}` : MESSAGES.cannotBeUploaded;
        case ROW_STATES.REMOVED:
            return MESSAGES.removed;
        default:
            return row.message || MESSAGES.failed;
    }
}

const SUBMITTABLE_STATES = [ROW_STATES.CHECKING, ROW_STATES.SCANNED];

export function submitBlockers(rows) {
    return (rows || []).filter((row) => !SUBMITTABLE_STATES.includes(row.state));
}

export function canSubmit(rows) {
    return submitBlockers(rows).length === 0;
}

export function submitBlockedMessage(blockers) {
    const names = (rows) => rows.map((row) => row.fileName).join(', ');
    const uploading = (blockers || []).filter((row) => row.state === ROW_STATES.UPLOADING);
    const unusable = (blockers || []).filter((row) => row.state !== ROW_STATES.UPLOADING);
    const parts = [];
    if (uploading.length > 0) parts.push(`Please wait for ${names(uploading)} to finish uploading.`);
    if (unusable.length > 0) parts.push(`Please remove or replace ${names(unusable)} before sending.`);
    return parts.join(' ');
}

export function documentsForSubmit(rows) {
    return (rows || [])
        .filter((row) => SUBMITTABLE_STATES.includes(row.state) && row.documentDomainId)
        .map((row) => ({ documentDomainId: row.documentDomainId, fileName: row.fileName }));
}

export function slotsUsed(rows) {
    return (rows || []).filter((row) => row.state !== ROW_STATES.REMOVED).length;
}

export function draftDocuments(draft) {
    return Array.isArray(draft?.documents)
        ? draft.documents.filter((d) => d && d.documentDomainId && d.fileName)
        : [];
}

export function addDraftDocument(draft, document) {
    const documents = draftDocuments(draft).filter((d) => d.documentDomainId !== document.documentDomainId);
    return { ...draft, documents: [...documents, { documentDomainId: document.documentDomainId, fileName: document.fileName }] };
}

export function removeDraftDocument(draft, documentDomainId) {
    return { ...draft, documents: draftDocuments(draft).filter((d) => d.documentDomainId !== documentDomainId) };
}

export function rowFromStatus(document, statusData) {
    if (!statusData) {
        return { key: document.documentDomainId, documentDomainId: document.documentDomainId, fileName: document.fileName, state: ROW_STATES.REMOVED };
    }
    return {
        key: document.documentDomainId,
        documentDomainId: document.documentDomainId,
        fileName: document.fileName,
        state: rowStateFor(statusData.status, statusData.result),
        reason: statusData.reason || null,
        statusSequence: statusData.statusSequence || 0,
    };
}

export function applyStatus(row, statusData) {
    if (!row || !statusData) return row;
    if ((statusData.statusSequence || 0) < (row.statusSequence || 0)) return row;
    return {
        ...row,
        state: rowStateFor(statusData.status, statusData.result),
        reason: statusData.reason || null,
        statusSequence: statusData.statusSequence || 0,
        progress: undefined,
        message: undefined,
    };
}
